import sha from 'sha.js';

export const OPENAI_ADS_SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

export type OpenAiAdsEventName =
  'page_viewed' | 'contents_viewed' | 'items_added' | 'checkout_started' | 'order_created';

export type OpenAiAdsContent = {
  id?: string;
  name?: string;
  content_type: 'product';
  quantity: number;
  amount?: number;
  currency?: string;
};

export type OpenAiAdsEventData = {
  type: 'contents';
  amount?: number;
  currency?: string;
  contents?: OpenAiAdsContent[];
};

export type OpenAiAdsEvent = {
  name: OpenAiAdsEventName;
  data: OpenAiAdsEventData;
  eventId?: string;
};

type Properties = Record<string, unknown>;

const DECIMAL_BASE = 10;
const DEFAULT_FRACTION_DIGITS = 2;
const CURRENCY_CODE_LENGTH = 3;
const MAX_NAME_LENGTH = 100;

const fractionDigitsByCurrency = new Map<string, number>();

const fractionDigitsFor = (currency: string) => {
  const cached = fractionDigitsByCurrency.get(currency);
  if (cached !== undefined) return cached;

  let digits: number;
  try {
    digits =
      new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? DEFAULT_FRACTION_DIGITS;
  } catch {
    digits = DEFAULT_FRACTION_DIGITS;
  }
  fractionDigitsByCurrency.set(currency, digits);
  return digits;
};

export const toMinorUnits = (price: unknown, currency: string): number | undefined => {
  if (price == null || price === '') return undefined;
  const value = Number(price);
  if (!Number.isFinite(value)) return undefined;
  return Math.round(value * DECIMAL_BASE ** fractionDigitsFor(currency));
};

const normalizeCurrency = (currency: unknown): string | undefined => {
  if (typeof currency !== 'string') return undefined;
  const trimmed = currency.trim().toUpperCase();
  return trimmed.length === CURRENCY_CODE_LENGTH ? trimmed : undefined;
};

const toPositiveInteger = (value: unknown, fallback: number) => {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const toOptionalString = (value: unknown) =>
  value == null || value === '' ? undefined : String(value);

export const withoutUndefined = <T extends object>(object: T): T =>
  Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined)) as T;

export const hashEmail = (email: string) =>
  sha('sha256').update(email.trim().toLowerCase()).digest('hex');

const toName = (value: unknown) => toOptionalString(value)?.slice(0, MAX_NAME_LENGTH);

const toContent = (product: Properties, fallbackCurrency?: string): OpenAiAdsContent => {
  const currency = normalizeCurrency(product.currency) ?? fallbackCurrency;
  return withoutUndefined({
    id: toOptionalString(product.product_id),
    name: toName(product.name),
    content_type: 'product',
    quantity: toPositiveInteger(product.quantity, 1),
    amount: currency ? toMinorUnits(product.price, currency) : undefined,
    currency
  });
};

const productsOf = (properties: Properties): Properties[] =>
  Array.isArray(properties.products)
    ? properties.products.filter((product): product is Properties => product != null)
    : [];

const sumContents = (contents: OpenAiAdsContent[]) => {
  const priced = contents.filter(content => content.amount != null);
  return priced.length
    ? priced.reduce((total, content) => total + (content.amount ?? 0) * content.quantity, 0)
    : undefined;
};

const contentsData = (
  contents: OpenAiAdsContent[],
  total: unknown,
  currency: string | undefined
): OpenAiAdsEventData => {
  const amount =
    currency == null
      ? undefined
      : (toMinorUnits(total, currency) ?? (contents.length ? sumContents(contents) : undefined));

  return withoutUndefined({
    type: 'contents',
    amount,
    currency: amount == null ? undefined : currency,
    contents: contents.length ? contents : undefined
  });
};

const singleProductEvent = (name: OpenAiAdsEventName, properties: Properties): OpenAiAdsEvent => {
  const currency = normalizeCurrency(properties.currency);
  const content = toContent({ ...properties, quantity: 1 }, currency);
  return { name, data: contentsData([content], undefined, currency) };
};

const cartEvent = (name: OpenAiAdsEventName, properties: Properties): OpenAiAdsEvent => {
  const products = productsOf(properties);
  const currency =
    normalizeCurrency(properties.currency) ?? normalizeCurrency(products[0]?.currency);
  const contents = products.map(product => toContent(product, currency));
  return { name, data: contentsData(contents, properties.total, currency) };
};

export const orderEventId = (orderId: unknown) =>
  orderId == null || orderId === '' ? undefined : `order_${orderId}`;

export const mapSegmentTrackToOpenAiAdsEvent = (
  eventName: string,
  properties: Properties = {}
): OpenAiAdsEvent | null => {
  switch (eventName) {
    case 'Product Viewed':
      return singleProductEvent('contents_viewed', properties);
    case 'Product Added':
      return properties.product_id == null
        ? { name: 'items_added', data: { type: 'contents' } }
        : singleProductEvent('items_added', properties);
    case 'Checkout Started':
      return cartEvent('checkout_started', properties);
    case 'Order Converted':
      return {
        ...cartEvent('order_created', properties),
        eventId: orderEventId(properties.order_id)
      };
    default:
      return null;
  }
};
